import { Alert, Center, Loader } from '@mantine/core';
import type { ReactNode } from 'react';
import { useSubscriptionStatus } from '../lib/hooks/useSubscriptionStatus';
import { UpsellCard } from './UpsellCard';

/**
 * Wraps any write-side UI surface with the account's subscription gate.
 * Renders children when the account is subscribed and writes are allowed;
 * renders {@link UpsellCard} when the gate is lapsed; renders a loader while
 * the status query is in-flight (no flicker of the gated content); renders
 * an error alert if the lookup fails outright.
 *
 * Shell: (desktop). Mandatory wrapper for every staff-facing screen that
 * performs writes (see CLAUDE.md "Every product-touching write surface uses
 * SubscriptionGate" — added with Epic 8).
 *
 * Non-obvious contracts:
 *   - Returns null during the loading phase rather than showing a spinner
 *     while ALSO mounting children, because mounting gated children would
 *     fire their data hooks against an account that may turn out to be lapsed.
 *   - `null` accountId is rendered as the "no account selected" alert — same as an
 *     account the user is not a member of (RLS returns null either way).
 */
export interface SubscriptionGateProps {
  accountId: string | null;
  children: ReactNode;
}

export function SubscriptionGate({ accountId, children }: SubscriptionGateProps) {
  const { data, isLoading, isError, error } = useSubscriptionStatus(accountId);

  if (!accountId) {
    return (
      <Alert color="yellow" title="No account selected">
        Sign in to an account to manage its inventory.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    );
  }

  if (isError) {
    return (
      <Alert color="red" title="Could not load subscription status">
        {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert color="yellow" title="Account not found">
        This account is not visible to you. If you believe this is a mistake, contact your owner or{' '}
        <a href="mailto:support@mytcgbinder.com">support</a>.
      </Alert>
    );
  }

  if (!data.writesAllowed) {
    return <UpsellCard tierId={data.tierId} status={data.status} />;
  }

  return <>{children}</>;
}
